/** Thin fetch wrappers for the custom login endpoints. */

export interface CredentialsPayload {
  email: string;
  password: string;
  /** Set only when the language switcher was used; saved to the profile on sign-in. */
  lang?: string;
}

export interface MfaPayload {
  session: string;
  code: string;
  challengeName: string;
  email: string;
  lang?: string;
}

/** The MFA methods Cognito can offer, matching the API's MFAType enum. */
export type MfaType = 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA';

export type LoginResult =
  | { status: 'OK' }
  | { status: 'MFA_REQUIRED'; challengeName: string; session: string; destination: string | null }
  | { status: 'NEW_PASSWORD_REQUIRED'; session: string }
  /** The account must enrol an authenticator before it can sign in at all. */
  | { status: 'MFA_SETUP_REQUIRED'; session: string; challengeName: string }
  /** More than one method is enrolled and none is preferred. */
  | { status: 'SELECT_MFA_TYPE'; session: string; challengeName: string; options: MfaType[] };

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message ?? `HTTP ${res.status}`);
  }
  return res.json();
}

export const loginWithCredentials = (payload: CredentialsPayload): Promise<LoginResult> =>
  post('/api/auth/credentials', payload);

export const loginWithMfa = (payload: MfaPayload): Promise<LoginResult> => post('/api/auth/mfa', payload);

export const loginWithNewPassword = (payload: {
  session: string;
  email: string;
  newPassword: string;
  lang?: string;
}): Promise<LoginResult> => post('/api/auth/new-password', payload);

/** Begin TOTP enrolment: returns the shared secret for the QR code and the next session. */
export const startMfaSetup = (session: string): Promise<{ status: 'MFA_SETUP'; secretCode: string; session: string }> =>
  post('/api/auth/mfa-setup', { session });

/** Finish enrolment with the authenticator's first code; signs the user in on success. */
export const completeMfaSetup = (payload: {
  session: string;
  email: string;
  code: string;
  lang?: string;
}): Promise<LoginResult> => post('/api/auth/mfa-setup/verify', payload);

/** Choose a method; the response is the chosen method's own challenge, not tokens. */
export const selectMfaType = (payload: {
  session: string;
  email: string;
  mfaType: MfaType;
}): Promise<LoginResult> => post('/api/auth/mfa-select', payload);

export const initiateSso = (email: string): Promise<{ url: string }> => post('/api/auth/sso', { email });

/** Resolves to a bare OK for any email: the response must not reveal whether an account exists. */
export const requestPasswordReset = (email: string): Promise<{ status: 'OK' }> =>
  post('/api/auth/forgot-password', { email });

export const confirmPasswordReset = (payload: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<{ status: 'OK' }> => post('/api/auth/reset-password', payload);
